import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Alert, Space, Typography, Row, Col, Collapse, Tag, Spin, Empty, Modal, Switch } from 'antd';
import { SearchOutlined, LinkOutlined, MedicineBoxOutlined, HeartOutlined, ExperimentOutlined, TranslationOutlined, GlobalOutlined, FileTextOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * MedlinePlus Connect Integration Component
 * Provides embedded access to official MedlinePlus health information
 * with Vietnamese translation support
 */
const MedlinePlusConnect = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [language, setLanguage] = useState('en'); // 'en' or 'es' for MedlinePlus, 'vi' for translation
  const [translatedContent, setTranslatedContent] = useState(null);
  const [translating, setTranslating] = useState(false);

  // Search MedlinePlus using our backend API
  const searchMedlinePlus = async (term) => {
    if (!term.trim()) return;

    setLoading(true);
    setResults([]);

    try {
      console.log(`🔍 Searching for: "${term}"`);

      // Use our backend API for MedlinePlus search
      const response = await axios.post('/medlineplus/search-by-term-test', {
        category: 'conditions', // Default category
        searchTerm: term.trim(),
        translate: language === 'vi',
        useCache: true
      });

      if (response.data.success && response.data.data) {
        const healthInfo = response.data.data[0]; // Get first result

        // Format for display
        const formattedResults = [{
          title: healthInfo.originalContent?.title || `Health Information: ${term}`,
          summary: healthInfo.originalContent?.summary || 'Health information from MedlinePlus',
          link: healthInfo.originalContent?.url || `https://medlineplus.gov/search?query=${encodeURIComponent(term)}`,
          type: getContentTypeLabel(healthInfo.contentType),
          fullContent: healthInfo.originalContent,
          vietnameseContent: healthInfo.vietnameseContent,
          codeSystem: healthInfo.codeSystem,
          code: healthInfo.code,
          codeName: healthInfo.codeName
        }];

        setResults(formattedResults);
      } else {
        // No results found
        setResults([]);
      }
    } catch (error) {
      console.error('MedlinePlus search error:', error);

      // Show user-friendly error with fallback
      setResults([{
        title: `Search: ${term}`,
        summary: `We're having trouble finding information about "${term}" right now. Please try a different search term or check your internet connection.`,
        link: `https://medlineplus.gov/search?query=${encodeURIComponent(term)}`,
        type: 'Search Result',
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user-friendly content type labels
  const getContentTypeLabel = (contentType) => {
    const typeLabels = {
      'diagnosis': 'Medical Condition',
      'medication': 'Medication',
      'lab_test': 'Lab Test',
      'procedure': 'Medical Procedure'
    };
    return typeLabels[contentType] || 'Health Topic';
  };

  // Get detailed content for a result
  const getDetailedContent = async (result) => {
    if (!result.fullContent) return result;

    try {
      // If we have enhanced content, use it
      if (result.fullContent.enhancedContent) {
        return {
          ...result,
          detailedContent: result.fullContent.enhancedContent
        };
      }

      // Otherwise return what we have
      return result;
    } catch (error) {
      console.error('Error getting detailed content:', error);
      return result;
    }
  };

  // Handle Vietnamese content display
  const getVietnameseContent = (result) => {
    if (result.vietnameseContent) {
      return {
        title: result.vietnameseContent.title || result.title,
        summary: result.vietnameseContent.summary || result.summary
      };
    }
    return null;
  };

  // View detailed result in modal
  const viewResult = async (result) => {
    setLoading(true);

    try {
      // Get detailed content
      const detailedResult = await getDetailedContent(result);
      setSelectedResult(detailedResult);
      setModalVisible(true);

      // Set Vietnamese content if available
      if (language === 'vi') {
        const viContent = getVietnameseContent(detailedResult);
        setTranslatedContent(viContent);
      } else {
        setTranslatedContent(null);
      }
    } catch (error) {
      console.error('Error viewing result:', error);
      setSelectedResult(result);
      setModalVisible(true);
      setTranslatedContent(null);
    } finally {
      setLoading(false);
    }
  };

  // Quick search buttons for common terms
  const quickSearchTerms = [
    { term: 'heart failure', icon: '❤️' },
    { term: 'diabetes', icon: '🩺' },
    { term: 'high blood pressure', icon: '🫀' },
    { term: 'chest pain', icon: '💔' },
    { term: 'asthma', icon: '🫁' },
    { term: 'depression', icon: '🧠' },
    { term: 'arthritis', icon: '🦴' },
    { term: 'covid-19', icon: '🦠' }
  ];

  // Professional content formatting matching MedlinePlus website style
  const renderFormattedContent = (content) => {
    if (!content) return null;

    // Advanced content parsing to match MedlinePlus structure
    let processedContent = content;

    // First, identify and mark section headers with special patterns
    processedContent = processedContent
      // Mark questions as headers
      .replace(/^(What is [^?]+\?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(What causes? [^?]+\?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(What are the [^?]+\?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(How is [^?]+\?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(Who is at risk [^?]+\?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(When should [^?]+\?)/gm, '###HEADER###$1###ENDHEADER###')
      // Mark other common medical headers
      .replace(/^(Symptoms?:?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(Treatment:?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(Diagnosis:?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(Prevention:?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(Risk [Ff]actors?:?)/gm, '###HEADER###$1###ENDHEADER###')
      .replace(/^(Complications?:?)/gm, '###HEADER###$1###ENDHEADER###');

    // Break long paragraphs at logical points
    if (content.length > 800 && !content.includes('\n\n')) {
      processedContent = processedContent
        // Break before questions
        .replace(/\. (What [a-z])/g, '.\n\n$1')
        .replace(/\. (How [a-z])/g, '.\n\n$1')
        .replace(/\. (Who [a-z])/g, '.\n\n$1')
        .replace(/\. (When [a-z])/g, '.\n\n$1')
        .replace(/\. (Where [a-z])/g, '.\n\n$1')
        .replace(/\. (Why [a-z])/g, '.\n\n$1')
        // Break before key medical concepts
        .replace(/\. (The symptoms? [a-z])/g, '.\n\n$1')
        .replace(/\. (Treatment [a-z])/g, '.\n\n$1')
        .replace(/\. (Your [a-z])/g, '.\n\n$1')
        .replace(/\. (If you [a-z])/g, '.\n\n$1')
        .replace(/\. (People with [a-z])/g, '.\n\n$1')
        .replace(/\. (Risk factors [a-z])/g, '.\n\n$1');
    }

    // Split into sections and process each
    const sections = processedContent.split(/\n\s*\n/).filter(section => section.trim());

    return sections.map((section, sectionIndex) => {
      const trimmedSection = section.trim();

      // Check if this section contains a header
      if (trimmedSection.includes('###HEADER###')) {
        const headerMatch = trimmedSection.match(/###HEADER###([^#]+)###ENDHEADER###/);
        if (headerMatch) {
          const headerText = headerMatch[1].trim();
          const remainingContent = trimmedSection.replace(/###HEADER###[^#]+###ENDHEADER###/, '').trim();

          return (
            <div key={sectionIndex} style={{ marginBottom: '24px' }}>
              {/* Section Header */}
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1890ff',
                marginBottom: '12px',
                marginTop: sectionIndex > 0 ? '20px' : '0',
                borderBottom: '1px solid #e8e8e8',
                paddingBottom: '8px'
              }}>
                {headerText}
              </h3>

              {/* Section Content */}
              {remainingContent && renderSectionContent(remainingContent)}
            </div>
          );
        }
      }

      // Regular content without header
      return (
        <div key={sectionIndex} style={{ marginBottom: '16px' }}>
          {renderSectionContent(trimmedSection)}
        </div>
      );
    });
  };

  // Helper function to render content within a section
  const renderSectionContent = (content) => {
    if (!content) return null;

    // Check if content contains bullet points or lists
    const lines = content.split('\n').map(line => line.trim()).filter(line => line);

    // Detect if this is a list
    const isList = lines.some(line =>
      line.match(/^[•·▪▫‣⁃]\s/) || // Bullet points
      line.match(/^\d+\.\s/) ||    // Numbered lists
      line.match(/^[a-zA-Z]\.\s/) || // Lettered lists
      line.match(/^-\s/) ||        // Dash lists
      line.match(/^\*\s/)          // Asterisk lists
    );

    if (isList) {
      // Separate list items from regular text
      const listItems = [];
      const regularText = [];

      lines.forEach(line => {
        if (line.match(/^[•·▪▫‣⁃\d+a-zA-Z\-\*]\s/) || line.match(/^\d+\.\s/)) {
          // This is a list item
          const cleanItem = line.replace(/^[•·▪▫‣⁃\d+a-zA-Z\-\*\.]\s*/, '').trim();
          if (cleanItem) listItems.push(cleanItem);
        } else {
          // This is regular text
          regularText.push(line);
        }
      });

      return (
        <div>
          {/* Regular text before list */}
          {regularText.length > 0 && (
            <p style={{
              marginBottom: '12px',
              lineHeight: '1.6',
              fontSize: '14px',
              color: '#333'
            }}>
              {regularText.join(' ')}
            </p>
          )}

          {/* List items */}
          {listItems.length > 0 && (
            <ul style={{
              marginBottom: '16px',
              paddingLeft: '20px',
              listStyleType: 'disc'
            }}>
              {listItems.map((item, index) => (
                <li key={index} style={{
                  marginBottom: '8px',
                  lineHeight: '1.5',
                  fontSize: '14px',
                  color: '#333'
                }}>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    } else {
      // Regular paragraph content
      return (
        <p style={{
          marginBottom: '16px',
          lineHeight: '1.6',
          fontSize: '14px',
          color: '#333',
          textAlign: 'justify'
        }}>
          {content}
        </p>
      );
    }
  };

  // Translate content to Vietnamese using our backend API
  const translateContent = async (content) => {
    if (!content || language !== 'vi') return content;

    try {
      setTranslating(true);
      const response = await axios.post('/api/v1/translate', {
        text: content,
        targetLanguage: 'vi',
        sourceLanguage: 'en'
      });

      if (response.data.success) {
        return response.data.translatedText;
      }
    } catch (error) {
      console.warn('Translation failed, showing original content:', error);
    } finally {
      setTranslating(false);
    }

    return content;
  };

  // Translate section titles to Vietnamese
  const translateSectionTitle = (title) => {
    if (language !== 'vi') return title;

    const titleTranslations = {
      'Overview': 'Tổng quan',
      'Symptoms': 'Triệu chứng',
      'Causes': 'Nguyên nhân',
      'Diagnosis': 'Chẩn đoán',
      'Treatment': 'Điều trị',
      'Prevention': 'Phòng ngừa',
      'Risk Factors': 'Yếu tố nguy cơ',
      'Complications': 'Biến chứng',
      'When to See a Doctor': 'Khi nào cần gặp bác sĩ',
      'Living With': 'Sống chung với bệnh',
      'Prognosis': 'Tiên lượng',
      'Research': 'Nghiên cứu',
      'Related Information': 'Thông tin liên quan'
    };

    return titleTranslations[title] || title;
  };

  // Component for handling translated content display
  const TranslatedContent = ({ content, language, renderFormattedContent }) => {
    const [translatedText, setTranslatedText] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
      if (language === 'vi' && content) {
        setIsTranslating(true);
        translateContent(content).then(translated => {
          setTranslatedText(translated);
          setIsTranslating(false);
        });
      } else {
        setTranslatedText(null);
      }
    }, [content, language]);

    if (isTranslating) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="small" />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Đang dịch nội dung...
          </div>
        </div>
      );
    }

    const displayContent = language === 'vi' && translatedText ? translatedText : content;
    return renderFormattedContent(displayContent);
  };

  // Get color for link type tags
  const getLinkTypeColor = (type) => {
    switch (type) {
      case 'symptoms': return 'orange';
      case 'treatment': return 'green';
      case 'diagnosis': return 'blue';
      case 'prevention': return 'purple';
      case 'diabetes-related': return 'red';
      case 'related-topic': return 'cyan';
      default: return 'default';
    }
  };

  // Format link type for display
  const formatLinkType = (type) => {
    switch (type) {
      case 'symptoms': return 'Symptoms';
      case 'treatment': return 'Treatment';
      case 'diagnosis': return 'Diagnosis';
      case 'prevention': return 'Prevention';
      case 'diabetes-related': return 'Diabetes';
      case 'related-topic': return 'Related';
      default: return 'Info';
    }
  };

  // Medical code examples
  const codeExamples = [
    {
      category: 'Diagnosis Codes (ICD-10-CM)',
      icon: <HeartOutlined />,
      examples: [
        { code: 'I50.9', description: 'Heart Failure', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.90&mainSearchCriteria.v.c=I50.9&informationRecipient.languageCode.c=en' },
        { code: 'E11.9', description: 'Type 2 Diabetes', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.90&mainSearchCriteria.v.c=E11.9&informationRecipient.languageCode.c=en' },
        { code: 'J45.9', description: 'Asthma', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.90&mainSearchCriteria.v.c=J45.9&informationRecipient.languageCode.c=en' }
      ]
    },
    {
      category: 'Medication Codes (RXCUI)',
      icon: <MedicineBoxOutlined />,
      examples: [
        { code: '6809', description: 'Metformin', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.88&mainSearchCriteria.v.c=6809&informationRecipient.languageCode.c=en' },
        { code: '29046', description: 'Lisinopril', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.88&mainSearchCriteria.v.c=29046&informationRecipient.languageCode.c=en' },
        { code: '1191', description: 'Aspirin', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.88&mainSearchCriteria.v.c=1191&informationRecipient.languageCode.c=en' }
      ]
    },
    {
      category: 'Lab Test Codes (LOINC)',
      icon: <ExperimentOutlined />,
      examples: [
        { code: '4548-4', description: 'Hemoglobin A1c', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.1&mainSearchCriteria.v.c=4548-4&informationRecipient.languageCode.c=en' },
        { code: '33747-0', description: 'Blood Glucose', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.1&mainSearchCriteria.v.c=33747-0&informationRecipient.languageCode.c=en' },
        { code: '57698-3', description: 'Lipid Panel', url: 'https://connect.medlineplus.gov/application?mainSearchCriteria.v.cs=2.16.840.1.113883.6.1&mainSearchCriteria.v.c=57698-3&informationRecipient.languageCode.c=en' }
      ]
    }
  ];

  return (
    <div>
      <Card title="🔍 Health Information Search" style={{ marginBottom: 16 }}>
        <Alert
          message="Official MedlinePlus Health Information"
          description="Search the most trusted health information from the U.S. National Library of Medicine. Get evidence-based, peer-reviewed content for patient education."
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* Main Search Interface */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              Search Health Information
            </Title>

            {/* Language Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <GlobalOutlined />
              <Switch
                checkedChildren="VI"
                unCheckedChildren="EN"
                checked={language === 'vi'}
                onChange={(checked) => setLanguage(checked ? 'vi' : 'en')}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {language === 'vi' ? 'Tiếng Việt' : 'English'}
              </Text>
            </div>
          </div>

          <Input.Search
            placeholder={language === 'vi'
              ? "Nhập tên bệnh, thuốc, thủ thuật hoặc triệu chứng (ví dụ: suy tim, tiểu đường, đau ngực)"
              : "Enter condition, medication, procedure, or symptom (e.g., heart failure, diabetes, chest pain)"
            }
            enterButton={
              <Button type="primary" icon={<SearchOutlined />} loading={loading}>
                {language === 'vi' ? 'Tìm kiếm' : 'Search MedlinePlus'}
              </Button>
            }
            size="large"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={searchMedlinePlus}
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Text type="secondary">
              {language === 'vi' ? 'Tìm kiếm nhanh:' : 'Quick searches:'}
            </Text>
            {quickSearchTerms.map(({ term, icon }) => (
              <Button
                key={term}
                size="small"
                type="link"
                onClick={() => searchMedlinePlus(term)}
                style={{ padding: '0 8px', height: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span>{icon}</span>
                {term}
              </Button>
            ))}
          </div>
        </div>

        {/* Search Results */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                {language === 'vi' ? 'Đang tìm kiếm thông tin y tế...' : 'Searching health information...'}
              </Text>
            </div>
          </div>
        )}

        {!loading && results.length === 0 && searchTerm && (
          <Empty
            description={language === 'vi'
              ? "Không tìm thấy kết quả. Hãy thử từ khóa khác."
              : "No results found. Try different search terms."
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {!loading && results.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5} style={{ marginBottom: 16 }}>
              {language === 'vi' ? 'Kết quả tìm kiếm:' : 'Search Results:'}
            </Title>
            <Row gutter={[16, 16]}>
              {results.map((result, index) => (
                <Col xs={24} md={12} lg={8} key={index}>
                  <Card
                    hoverable
                    size="small"
                    title={
                      <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                        {language === 'vi' && result.vietnameseContent?.title
                          ? result.vietnameseContent.title
                          : result.title}
                      </div>
                    }
                    extra={
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <Tag color={result.isError ? 'red' : 'blue'} style={{ fontSize: '10px' }}>
                          {result.type || 'Health Info'}
                        </Tag>
                        {result.codeSystem && result.code && (
                          <Tag color="green" style={{ fontSize: '10px' }}>
                            {result.codeSystem}: {result.code}
                          </Tag>
                        )}
                      </div>
                    }
                    actions={[
                      <Button
                        type="primary"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => viewResult(result)}
                        disabled={result.isError}
                      >
                        {language === 'vi' ? 'Xem chi tiết' : 'View Details'}
                      </Button>
                    ]}
                    style={{
                      borderColor: result.isError ? '#ff4d4f' : undefined,
                      backgroundColor: result.isError ? '#fff2f0' : undefined
                    }}
                  >
                    <Text
                      type={result.isError ? 'danger' : 'secondary'}
                      style={{ fontSize: '12px', display: 'block' }}
                    >
                      {language === 'vi' && result.vietnameseContent?.summary
                        ? result.vietnameseContent.summary.substring(0, 120)
                        : result.summary?.substring(0, 120)}
                      {result.summary?.length > 120 ? '...' : ''}
                    </Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Benefits Section */}
        <Alert
          message="Why Use MedlinePlus Connect?"
          description={
            <div>
              <Row gutter={[16, 8]}>
                <Col xs={24} md={12}>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>✅ Evidence-based, peer-reviewed content</li>
                    <li>✅ Always up-to-date medical information</li>
                    <li>✅ Professional medical terminology</li>
                  </ul>
                </Col>
                <Col xs={24} md={12}>
                  <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                    <li>✅ Available in English and Spanish</li>
                    <li>✅ Comprehensive health topic coverage</li>
                    <li>✅ Print-friendly patient education materials</li>
                  </ul>
                </Col>
              </Row>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Card>

      {/* Advanced Search Options */}
      <Card title="🔧 Advanced Search Options" style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          For healthcare professionals: Search using specific medical codes for precise results.
        </Text>
        
        <Collapse ghost>
          {codeExamples.map((category, index) => (
            <Panel 
              header={
                <span>
                  {category.icon} {category.category}
                </span>
              } 
              key={index}
            >
              <div style={{ background: '#fafafa', padding: 16, borderRadius: 6 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {category.examples.map((example, exampleIndex) => (
                    <div key={exampleIndex} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Tag color="blue">{example.code}</Tag>
                        <Text>{example.description}</Text>
                      </div>
                      <Button
                        type="primary"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => window.open(example.url, '_blank')}
                      >
                        Search
                      </Button>
                    </div>
                  ))}
                </Space>
              </div>
            </Panel>
          ))}
        </Collapse>
      </Card>

      {/* Detailed Result Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HeartOutlined />
            {language === 'vi' ? 'Thông tin y tế chi tiết' : 'Detailed Health Information'}
            {language === 'vi' && (
              <Tag color="green" icon={<TranslationOutlined />}>
                Đã dịch
              </Tag>
            )}
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            {language === 'vi' ? 'Đóng' : 'Close'}
          </Button>,
          (selectedResult?.enhancedContent?.url || selectedResult?.link) && (
            <Button
              key="original"
              type="primary"
              icon={<LinkOutlined />}
              onClick={() => window.open(selectedResult.enhancedContent?.url || selectedResult.link, '_blank')}
            >
              {language === 'vi' ? 'Xem bản gốc' : 'View Original'}
            </Button>
          )
        ]}
        width={800}
      >
        {selectedResult && (
          <div>
            <Title level={4} style={{ marginBottom: 16 }}>
              {language === 'vi' && translatedContent?.title
                ? translatedContent.title
                : selectedResult.title}
            </Title>

            {/* Show medical code information */}
            {selectedResult.codeSystem && selectedResult.code && (
              <div style={{ marginBottom: 16, padding: 12, background: '#f0f8ff', borderRadius: 6, border: '1px solid #d6e4ff' }}>
                <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>
                  Medical Code: {selectedResult.codeSystem} {selectedResult.code}
                </Text>
                {selectedResult.codeName && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {selectedResult.codeName}
                    </Text>
                  </div>
                )}
              </div>
            )}

            {/* Professional content display matching MedlinePlus style */}
            {selectedResult.enhancedContent && selectedResult.enhancedContent.sections && selectedResult.enhancedContent.sections.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                {language === 'vi' && (
                  <Alert
                    message="Bản dịch tiếng Việt"
                    description="Thông tin này đã được dịch từ tiếng Anh. Vui lòng tham khảo bản gốc để có thông tin chính xác nhất."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                {/* Clean section-based display without collapsible panels */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {selectedResult.enhancedContent.sections.map((section, index) => (
                    <div key={index} style={{
                      borderBottom: index < selectedResult.enhancedContent.sections.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}>
                      {/* Section Header */}
                      <div style={{
                        padding: '16px 20px 12px 20px',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #e8e8e8'
                      }}>
                        <h3 style={{
                          margin: 0,
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1890ff',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <FileTextOutlined />
                          {translateSectionTitle(section.title)}
                          {translating && (
                            <Spin size="small" style={{ marginLeft: '8px' }} />
                          )}
                        </h3>
                      </div>

                      {/* Section Content */}
                      <div style={{
                        padding: '20px',
                        backgroundColor: '#ffffff'
                      }}>
                        <TranslatedContent
                          content={section.formattedContent || section.content}
                          language={language}
                          renderFormattedContent={renderFormattedContent}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Clean fallback content display */
              <div style={{ marginBottom: 16 }}>
                {language === 'vi' && (
                  <Alert
                    message="Bản dịch tiếng Việt"
                    description="Thông tin này đã được dịch từ tiếng Anh. Vui lòng tham khảo bản gốc để có thông tin chính xác nhất."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  padding: '20px'
                }}>
                  <TranslatedContent
                    content={selectedResult.summary || selectedResult.enhancedContent?.summary || selectedResult.fullContent?.summary || 'No detailed information available.'}
                    language={language}
                    renderFormattedContent={renderFormattedContent}
                  />
                </div>
              </div>
            )}

            {/* Additional enhanced content if available */}
            {selectedResult.detailedContent && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>
                  {language === 'vi' ? 'Thông tin bổ sung' : 'Additional Information'}
                </Title>
                <div style={{
                  background: '#fafafa',
                  padding: 16,
                  borderRadius: 6,
                  border: '1px solid #e8e8e8'
                }}>
                  <TranslatedContent
                    content={selectedResult.detailedContent}
                    language={language}
                    renderFormattedContent={renderFormattedContent}
                  />
                </div>
              </div>
            )}

            {/* Related links */}
            {(selectedResult.enhancedContent?.relatedLinks || selectedResult.fullContent?.relatedLinks) &&
             (selectedResult.enhancedContent?.relatedLinks || selectedResult.fullContent?.relatedLinks).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Related Health Topics</Title>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '12px',
                  marginTop: '12px'
                }}>
                  {(selectedResult.enhancedContent?.relatedLinks || selectedResult.fullContent?.relatedLinks).slice(0, 6).map((link, index) => (
                    <Card
                      key={index}
                      size="small"
                      hoverable
                      style={{
                        cursor: 'pointer',
                        border: '1px solid #e8e8e8',
                        borderRadius: '6px'
                      }}
                      onClick={() => window.open(link.url || link.href, '_blank')}
                      bodyStyle={{ padding: '12px' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <LinkOutlined style={{
                          color: '#1890ff',
                          fontSize: '14px',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#1890ff',
                            marginBottom: '4px',
                            lineHeight: '1.3'
                          }}>
                            {link.title}
                          </div>
                          {link.description && (
                            <div style={{
                              fontSize: '11px',
                              color: '#666',
                              lineHeight: '1.3'
                            }}>
                              {link.description}
                            </div>
                          )}
                          {link.type && (
                            <Tag
                              size="small"
                              color={getLinkTypeColor(link.type)}
                              style={{
                                fontSize: '10px',
                                marginTop: '4px',
                                padding: '0 4px'
                              }}
                            >
                              {formatLinkType(link.type)}
                            </Tag>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Professional source attribution */}
            <div style={{
              marginTop: 24,
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%)',
              borderRadius: '8px',
              border: '1px solid #d6e4ff',
              borderLeft: '4px solid #1890ff'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                  {language === 'vi' ? 'Nguồn thông tin' : 'Information Source'}
                </Text>
              </div>

              <Text style={{ fontSize: '13px', color: '#333', lineHeight: '1.5' }}>
                {language === 'vi'
                  ? 'MedlinePlus - Thư viện Y học Quốc gia Hoa Kỳ (NIH)'
                  : 'MedlinePlus - U.S. National Library of Medicine (NIH)'
                }
              </Text>

              {(selectedResult.enhancedContent?.attribution || selectedResult.fullContent?.attribution) && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e6f4ff' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                    {selectedResult.enhancedContent?.attribution || selectedResult.fullContent?.attribution}
                  </Text>
                </div>
              )}

              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e6f4ff' }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {language === 'vi'
                    ? 'Thông tin này chỉ mang tính chất giáo dục và không thay thế lời khuyên y tế chuyên nghiệp.'
                    : 'This information is for educational purposes only and does not replace professional medical advice.'
                  }
                </Text>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MedlinePlusConnect;
